import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Loader2, Trash2, Image as ImageIcon, Video, FileText, X } from 'lucide-react';
import api from '../../lib/api';

const FMT_ICON = { IMAGE: ImageIcon, VIDEO: Video, DOCUMENT: FileText };
const prettySize = (b) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round((b || 0) / 1024))} KB`);

/**
 * Reusable header-media library. Lists a tenant's uploaded images/videos/docs
 * (filtered to `format`) and, on pick, re-hosts a FRESH Meta handle so the media
 * can be attached to a new template without re-uploading.
 */
export const WhatsAppMediaLibrary = ({ format, onSelect, onClose }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [thumbs, setThumbs] = useState({});
  const [picking, setPicking] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/social-connectors/whatsapp/media-library');
      const all = r.data?.items || [];
      setItems(format ? all.filter((i) => i.format === format) : all);
    } catch {
      toast.error('Could not load media library');
    } finally {
      setLoading(false);
    }
  }, [format]);

  useEffect(() => { load(); }, [load]);

  // Lazily fetch image/video thumbnails as authenticated blobs.
  useEffect(() => {
    let revoked = [];
    items.forEach((it) => {
      if (thumbs[it.id] || it.format === 'DOCUMENT') return;
      api.get(`/api/social-connectors/whatsapp/media-library/${it.id}/content`, { responseType: 'blob' })
        .then((res) => {
          const url = URL.createObjectURL(res.data);
          revoked.push(url);
          setThumbs((t) => ({ ...t, [it.id]: url }));
        }).catch(() => {});
    });
    return () => { revoked.forEach((u) => { try { URL.revokeObjectURL(u); } catch { /* ignore */ } }); };
  }, [items]);

  const pick = async (it) => {
    setPicking(it.id);
    try {
      const r = await api.post(`/api/social-connectors/whatsapp/media-library/${it.id}/handle`);
      const d = r.data || {};
      if (!d.header_handle) throw new Error('no handle');
      onSelect({
        format: d.format, header_handle: d.header_handle, storage_key: d.storage_key,
        filename: d.filename, mime: d.mime, width: it.width, height: it.height,
        previewUrl: thumbs[it.id] || '',
      });
    } catch (e) {
      toast.error('Could not use this media', { description: e?.response?.data?.detail || 'Try again' });
    } finally {
      setPicking('');
    }
  };

  const remove = async (it, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${it.filename}" from your library?`)) return;
    try {
      await api.delete(`/api/social-connectors/whatsapp/media-library/${it.id}`);
      setItems((prev) => prev.filter((x) => x.id !== it.id));
      toast.success('Removed from library');
    } catch {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="rounded-md border border-border bg-background p-2" data-testid="wa-media-library">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-[11px] font-medium">Choose from library {format ? `· ${format.toLowerCase()}` : ''}</p>
        <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={onClose} data-testid="wa-media-library-close"><X className="h-3.5 w-3.5" /></Button>
      </div>
      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : items.length === 0 ? (
        <p className="py-4 text-center text-[11px] text-muted-foreground" data-testid="wa-media-library-empty">No saved {format ? format.toLowerCase() : 'media'} yet — upload one and it'll appear here.</p>
      ) : (
        <div className="grid grid-cols-3 gap-2" data-testid="wa-media-library-grid">
          {items.map((it) => {
            const Icon = FMT_ICON[it.format] || ImageIcon;
            return (
              <button key={it.id} type="button" onClick={() => pick(it)} disabled={!!picking}
                className="group relative overflow-hidden rounded-md border border-border text-left hover:border-primary" data-testid={`wa-media-item-${it.id}`}>
                <div className="flex h-20 items-center justify-center bg-slate-100">
                  {it.format === 'IMAGE' && thumbs[it.id] ? (
                    <img src={thumbs[it.id]} alt={it.filename} className="h-full w-full object-cover" />
                  ) : it.format === 'VIDEO' && thumbs[it.id] ? (
                    <video src={thumbs[it.id]} className="h-full w-full object-cover" muted />
                  ) : (
                    <Icon className="h-7 w-7 text-slate-400" />
                  )}
                  {picking === it.id && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30"><Loader2 className="h-5 w-5 animate-spin text-white" /></div>
                  )}
                </div>
                <div className="p-1">
                  <p className="truncate text-[10px] font-medium">{it.filename}</p>
                  <p className="text-[9px] text-muted-foreground">
                    {it.width && it.height ? `${it.width}×${it.height} · ` : ''}{prettySize(it.size)} · used {it.used_count || 0}×
                  </p>
                </div>
                <span onClick={(e) => remove(it, e)} className="absolute right-1 top-1 rounded bg-white/90 p-0.5 text-red-500 shadow-sm hover:bg-white" data-testid={`wa-media-delete-${it.id}`}>
                  <Trash2 className="h-3 w-3" />
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default WhatsAppMediaLibrary;
